import { z } from 'zod';
import type { OperatorKind } from './drone-marketplace';

/**
 * Drone marketplace PRICING — the operator RATE CARD, the per-mission ESTIMATE, and the
 * competitive TIER ($0 gov / at-cost / market). Drone pricing #344 (migration 0096).
 *
 * SCOPE: DISPLAY + ESTIMATE ONLY. There is NO charging logic and NO payment rails here —
 * these are PURE, side-effect-free helpers shared by the API (the matcher + the operator
 * portal) and the web so the estimate math + the tier label can never drift between them
 * (same discipline as computeDroneFitScore / deriveCredentialExpiryFlag). The final
 * quoted_price_cad is still operator-stamped at accept-with-terms; the mission ledger
 * (mission_usage_ledger, 0089) is unchanged.
 *
 * MONEY GOTCHA: numeric(12,2) CAD columns arrive as STRINGS across the API boundary. Callers
 * Number() them BEFORE handing a RateCard to these helpers — never do arithmetic on strings.
 */

/** The agency-level rate card (migration 0096). All three NULLABLE: a simple operator sets
 *  just an hourly rate; mobilization is a fixed launch/callout fee; per_km covers transit.
 *  snake_case to match the API contract + the agencies column names (callers Number()-coerce
 *  the numeric(12,2) strings into these fields). */
export interface RateCard {
  mobilization_fee_cad: number | null;
  hourly_rate_cad: number | null;
  per_km_cad: number | null;
}

/** numeric(12,2) ceiling — 10 integer digits + 2 decimals. */
export const RATE_CARD_MAX_CAD = 9_999_999.99;

const RateField = z.number().nonnegative().max(RATE_CARD_MAX_CAD).nullable();

/** Body for the operator-portal rate-card PATCH. Every field optional (partial update) +
 *  nullable (send null to clear a rate); at least one must be present. Mirrors the
 *  ProfileUpdateSchema "at-least-one-field" guard. */
export const RateCardUpdateSchema = z
  .object({
    mobilization_fee_cad: RateField.optional(),
    hourly_rate_cad: RateField.optional(),
    per_km_cad: RateField.optional(),
  })
  .refine((b) => Object.keys(b).length > 0, { message: 'at least one rate field required' });
export type RateCardUpdate = z.infer<typeof RateCardUpdateSchema>;

/**
 * Competitive pricing tier. The marketplace's coverage-and-cost story:
 *   - gov     — a government / public-service fleet ⇒ $0 (no charge).
 *   - at-cost — an Ontario Corps volunteer recouping consumables (a low rate).
 *   - market  — a commercial / hobbyist operator setting its own rate.
 * Anti-clobber const-tuple (NOT a DB column / CHECK — the tier is DERIVED, not stored).
 */
export const OPERATOR_PRICING_TIERS = ['gov', 'at-cost', 'market'] as const;
export const OperatorPricingTierSchema = z.enum(OPERATOR_PRICING_TIERS);
export type OperatorPricingTier = z.infer<typeof OperatorPricingTierSchema>;

/** Short badge label per tier (web + investor demo). */
export const OPERATOR_PRICING_TIER_LABELS: Readonly<Record<OperatorPricingTier, string>> = {
  gov: '$0 · Government',
  'at-cost': 'At-cost',
  market: 'Market rate',
};

export function pricingTierLabel(tier: OperatorPricingTier): string {
  return OPERATOR_PRICING_TIER_LABELS[tier];
}

/**
 * Derive the competitive tier from the operator's EXISTING org markers — deliberately NOT a
 * price-magnitude threshold (which would be arbitrary), so the tier reflects WHO the operator
 * is, while the estimate reflects the price:
 *   - government_owned (#343)             → 'gov'     ($0)
 *   - operator_kind 'ontario_corps' (vol) → 'at-cost'
 *   - otherwise (private / commercial / null) → 'market'
 * PURE. The single source of truth shared by the matcher route + the web.
 */
export function deriveOperatorTier(input: {
  governmentOwned: boolean;
  operatorKind: OperatorKind | null;
}): OperatorPricingTier {
  if (input.governmentOwned) return 'gov';
  if (input.operatorKind === 'ontario_corps') return 'at-cost';
  return 'market';
}

/** True when the operator has published ANY rate (else the price is "rate not set", never $0). */
export function rateCardHasAnyRate(card: RateCard): boolean {
  return (
    card.mobilization_fee_cad != null || card.hourly_rate_cad != null || card.per_km_cad != null
  );
}

/**
 * Estimate a mission's price from the operator rate card + the requested on-station duration +
 * the matcher's transit distance. PURE.
 *   - government_owned                 ⇒ 0   (the $0 gov tier — "No charge").
 *   - no rate published                ⇒ null ("rate not set"; surface honestly, never $0).
 *   - a non-gov estimate that nets to 0 ⇒ null (insufficient inputs — e.g. an hourly-only card
 *                                          with no requested duration — never a misleading $0).
 *   - else  mobilization + hourly × (onStationMinutes/60) + per_km × transitKm  (rounded to cents)
 * Each component contributes only when set (an hourly-only card still estimates from duration).
 */
export function estimateMissionPrice(input: {
  rateCard: RateCard;
  onStationMinutes: number | null;
  transitKm: number | null;
  governmentOwned: boolean;
}): number | null {
  if (input.governmentOwned) return 0;
  if (!rateCardHasAnyRate(input.rateCard)) return null;
  const { mobilization_fee_cad, hourly_rate_cad, per_km_cad } = input.rateCard;
  const minutes =
    input.onStationMinutes != null && input.onStationMinutes > 0 ? input.onStationMinutes : 0;
  const transit = input.transitKm != null && input.transitKm > 0 ? input.transitKm : 0;
  const total =
    (mobilization_fee_cad ?? 0) +
    (hourly_rate_cad ?? 0) * (minutes / 60) +
    (per_km_cad ?? 0) * transit;
  const rounded = Math.round(total * 100) / 100;
  // A non-gov estimate of exactly 0 means there was nothing estimable (no fixed fee + no
  // priced duration/transit) — return null so the UI shows "rate not set", not "No charge".
  return rounded > 0 ? rounded : null;
}

/** Format a CAD amount. null ⇒ '—'; 0 ⇒ 'No charge' (only a gov $0 reaches 0); else '$1,234.50 CAD'. */
export function formatCad(amount: number | null): string {
  if (amount == null) return '—';
  if (amount === 0) return 'No charge';
  return `$${amount.toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} CAD`;
}
